/**
 * IndexedDB-backed cache for paginated report data.
 * Chosen over localStorage because report payloads can be several MB
 * (thousands of rows across multiple pages), which would silently exceed
 * the ~5 MB localStorage quota.
 *
 * Cache lifecycle:
 *  - Key includes today's date → a new day means a new key → stale entries
 *    are evicted automatically the next time any report is loaded.
 *  - Within the same day, data is served from IndexedDB on every reload
 *    without hitting the network.
 */

const DB_NAME = "revenue360-report-cache";
const DB_VERSION = 1;
const STORE_NAME = "reports";

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(STORE_NAME)) {
        request.result.createObjectStore(STORE_NAME);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function getCachedReport<T>(key: string): Promise<T | null> {
  try {
    const db = await openDB();
    return new Promise<T | null>((resolve) => {
      const tx = db.transaction(STORE_NAME, "readonly");
      const req = tx.objectStore(STORE_NAME).get(key);
      req.onsuccess = () => resolve((req.result as T) ?? null);
      req.onerror = () => resolve(null);
    });
  } catch {
    return null;
  }
}

export async function setCachedReport<T>(key: string, data: T): Promise<void> {
  try {
    const db = await openDB();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readwrite");
      tx.objectStore(STORE_NAME).put(data, key);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch {
    // silently skip — the hook will still return live data; it just won't persist
  }
}

export function clearReportCache(): Promise<void> {
  return new Promise((resolve) => {
    const req = indexedDB.deleteDatabase(DB_NAME);
    req.onsuccess = () => resolve();
    req.onerror = () => resolve();
    req.onblocked = () => resolve();
  });
}

/**
 * Removes all cached entries whose key does not match the current day's key.
 * Called once per hook invocation so stale entries never accumulate.
 */
export async function evictStaleReports(currentKey: string): Promise<void> {
  try {
    const db = await openDB();
    await new Promise<void>((resolve) => {
      const tx = db.transaction(STORE_NAME, "readwrite");
      const store = tx.objectStore(STORE_NAME);
      const req = store.getAllKeys();
      req.onsuccess = () => {
        (req.result as string[]).forEach((k) => {
          if (k !== currentKey) store.delete(k);
        });
        resolve();
      };
      req.onerror = () => resolve();
    });
  } catch {
    // ignore — eviction is best-effort
  }
}
